<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import DateInput from "../../../../../Components/Common/DateInput.svelte";

    import {
        InsuranceType,
        OOPType,
        BillingType,
    } from "../../../../../Utils/utilEnums.svelte";

    import {
        amaUnitValue,
        insuranceFund,
        mbsToAsaDisallowAsaByInsuranceType,
    } from "../../../../../stores";

    import {
        checkMedicareNoValidation,
        setInputFilter,
        getAmountInCurrency,
    } from "../../../../../Utils/utilFunctions.svelte";

    import { cacheInsuranceFunds } from "../../../../../Utils/utilCache.svelte";

    import {
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
        noNeedForMedicareNoArr,
        unknownPatientInsuranceType,
    } from "../../../../../Utils/utilConstants.svelte";

    import {
        Loader,
        NotificationModal,
        DecimalInput,
    } from "../../../../../Components/Common/index.svelte";

    export let isModalOpen = false;
    export let latestObjDetails = {};
    export let patientEncounterDetails = {};
    export let isUpdatingBillingDetails = false;
    export let type = "";
    export let itemsData = [];
    export let closeModal = () => {};
    export let updateLatestDetails = () => {};

    $: invalidDateRange =
        modalBillingData.startDate &&
        modalBillingData.endDate &&
        modalBillingData.startDate > modalBillingData.endDate;

    $: if (
        insuranceObj.insuranceType == 0 &&
        !$insuranceFund.length &&
        !isInsuranceFundFetched
    )
        _loadInsuranceFund();

    $: showCoverTypeForPrivateInsurance =
        insuranceObj.insuranceType == 0 &&
        insuranceObj.insuranceFund?.id &&
        (insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AMA" ||
            insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AUH");

    $: isAmaInsurance =
        showCoverTypeForPrivateInsurance &&
        insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AMA";

    $: isAuhInsurance =
        showCoverTypeForPrivateInsurance &&
        insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AUH";

    $: isValidMedicareNo =
        noNeedForMedicareNoArr.includes(Number(insuranceObj.insuranceType)) ||
        insuranceObj.insuranceType == unknownPatientInsuranceType
            ? true
            : checkMedicareNoValidation(insuranceObj.medicareNo);

    $: hasItemTypeChanged =
        __DefaultItemUseASA__ != modalBillingData.useAsaCodes;

    // determine to show confirm action modal or not
    $: hasBillingTypeFiguresChanged =
        hasItemTypeChanged ||
        hasInsuranceTypeChange ||
        modalBillingData.billingType != latestObjDetails.billingType ||
        modalBillingData.calculationAmount !=
            latestObjDetails.calculationAmount;

    // when insurance type changes or for private insurance, change fund would reset latest billing details actual gap, rebate and total fee to 0
    $: hasInsuranceTypeChange =
        insuranceObj.insuranceType == 0 && !hasInsuranceTypeChange
            ? latestObjDetails.patientInsuranceId !=
              insuranceObj.insuranceFund?.id
            : latestObjDetails.insuranceType != insuranceObj.insuranceType;

    $: isInsuranceTypeEnableSwitchItemType =
        _checkInsuranceTypeEnableSwitchItemType(
            insuranceObj.insuranceType,
            modalBillingData,
        );

    $: displayTotalFee = _getModalBillingDataTotalFee(
        modalBillingData.addGST,
        modalBillingData.actualGap,
        modalBillingData.rebate,
        modalBillingData.totalFee,
    );

    $: if (insuranceObj.insuranceType == 15)
        modalBillingData.addGST = Boolean(true);
    else modalBillingData.addGST = Boolean(false);

    $: insuranceAllowManuallyChangeBillingRule =
        (!insuranceTypesNotAllowBillingRule.includes(
            insuranceObj?.insuranceType,
        ) &&
            !insuranceTypesMayAllowAmaBillingRule.includes(
                insuranceObj.insuranceType,
            )) ||
        (insuranceTypesMayAllowAmaBillingRule.includes(
            insuranceObj.insuranceType,
        ) &&
            modalBillingData.usingAmaRates);

    $: isChangingBillingTypeDisabled =
        modalBillingData.oopType === 1 &&
        isPastEncounter(patientEncounterDetails.scheduledDate);

    $: hasTimeItem = itemsData.some(({ infoType }) => infoType == "Time");

    $: isWcOrTacUsingAmaRates =
        insuranceTypesMayAllowAmaBillingRule.includes(
            insuranceObj.insuranceType,
        ) && latestObjDetails.usingAmaRates;

    $: displayBillingTypes = _getDisplayBillingTypes(
        hasTimeItem,
        isWcOrTacUsingAmaRates,
    );

    let modalBillingData = {};

    let insuranceObj = {};

    let isLoadingInsuranceFund = false,
        isInsuranceFundFetched = false;

    let changeItemTypeModalOpen = false;

    let __DefaultItemUseASA__, __DefaultInsuranceType__;

    let isShowInsuranceItemConflictModal = false;

    // Private, Overseas, Self-insured, Cosmetic
    const restrictedInsuranceTypes = InsuranceType.filter((it) => it.id < 16);

    onMount(async () => {
        _syncBillingData();

        await _syncInsuranceData();

        updateInputFields();
    });

    const updateProcess = () => {
        if (!modalBillingData.startDate) modalBillingData.startDate = null;
        if (!modalBillingData.endDate) modalBillingData.endDate = null;

        if (!_checkInputFieldsValid()) _updateInvalidFieldsStyle();
        else
            updateLatestDetails(
                hasBillingTypeFiguresChanged,
                modalBillingData,
                insuranceObj,
                hasItemTypeChanged,
                hasInsuranceTypeChange,
            );
    };

    const updateInputFields = () => {
        setInputFilter(
            document.getElementById("medicareNoField"),
            function (value) {
                return value ? /^[0-9-\s]+$/i.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("medicareRefNoField"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("membershipNoInput"),
            function (value) {
                return value ? /^[A-z0-9]+$/.test(value) : true;
            },
            "Must be either an integer or a character",
        );

        setInputFilter(
            document.getElementById("membershipRefNoInput"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );
    };

    const handleItemsTypeChange = () => {
        if (insuranceObj.insuranceType !== undefined)
            __DefaultInsuranceType__ = insuranceObj.insuranceType;

        if (modalBillingData.id && !hasItemTypeChanged)
            openChangeItemTypeModal();
    };

    const changeItemType = () => {
        closeChangeItemTypeModal(true);
    };

    const openChangeItemTypeModal = () => (changeItemTypeModalOpen = true);

    const closeChangeItemTypeModal = (isChanged = false) => {
        changeItemTypeModalOpen = false;

        if (!isChanged)
            modalBillingData.useAsaCodes = !modalBillingData.useAsaCodes;
    };

    const openInsuranceItemConflictModal = () =>
        (isShowInsuranceItemConflictModal = true);

    const closeInsuranceItemConflictModal = (isChanged = false) => {
        isShowInsuranceItemConflictModal = false;

        if (isChanged) {
            __DefaultInsuranceType__ = insuranceObj.insuranceType;
            modalBillingData.useAsaCodes = false;
        } else if (insuranceObj.insuranceType != __DefaultInsuranceType__) {
            insuranceObj.insuranceType = __DefaultInsuranceType__;
            modalBillingData.useAsaCodes = true;
            if (
                type.toString().toLowerCase != "quote" &&
                insuranceObj.workerCompensationObj?.id
            )
                _clearWorkerCompensation();
        }
    };

    const handleChangeBillingType = () => {
        if (modalBillingData?.billingType == 1)
            modalBillingData.calculationAmount = 0;

        if (!insuranceAllowManuallyChangeBillingRule) {
            modalBillingData.calculationAmount = 0;
            modalBillingData.billingType = 3;
        }
    };

    // HELPER FUNCTIONS
    const _syncBillingData = () => {
        modalBillingData = {
            ...latestObjDetails,
            skipQuote: patientEncounterDetails.skipQuote,
        };

        if (!modalBillingData.id) {
            const { patient, skipQuote } = patientEncounterDetails;

            modalBillingData = {
                patientPaysTotalFee: Boolean(true),
                oopType: Number(0),
                isLatestBillingData: Boolean(true),
                skipQuote: skipQuote,
                addGST: Boolean(false),
                insuranceType: Number(patient.insuranceType),
                patientMedicareNo: patient.medicareNo,
                patientMedicareRefNo: patient.medicareRefNo,
                patientMembershipNo: patient.membershipNo,
                patientMembershipRefNo: patient.membershipRefNo,
                patientInsuranceId: patient.insuranceId,
                patientInsurance: patient.insurance,
                patientVeteranNo: patient.veteranNo,
                patientInsuranceState: patient.insuranceState,
            };
        }

        __DefaultItemUseASA__ =
            modalBillingData.useAsaCodes == undefined
                ? false
                : modalBillingData.useAsaCodes;
        __DefaultInsuranceType__ = modalBillingData.insuranceType;
    };

    const _syncInsuranceData = async () => {
        if (
            modalBillingData.insuranceType == 1 ||
            modalBillingData.insuranceType == 2
        )
            insuranceObj.insuranceType = 0;
        else insuranceObj.insuranceType = modalBillingData.insuranceType;

        insuranceObj.medicareNo = modalBillingData.patientMedicareNo ?? "";

        insuranceObj.medicareRefNo =
            modalBillingData.patientMedicareRefNo ?? "";

        insuranceObj.membershipNo = modalBillingData.patientMembershipNo ?? "";

        insuranceObj.membershipRefNo =
            modalBillingData.patientMembershipRefNo ?? "";

        switch (insuranceObj.insuranceType) {
            case 0: // Private
                insuranceObj.insuranceFundId =
                    modalBillingData.patientInsuranceId ?? Number(0);
                insuranceObj.privateCoverType =
                    modalBillingData.patientInsurance ?? "";
                await _loadInsuranceFund();
                break;
            case 10: // Overseas
                insuranceObj.overseasCover =
                    modalBillingData.patientInsurance ?? "";
                insuranceObj.insuranceFundId =
                    modalBillingData.patientInsuranceId ?? Number(0);
                break;
            case 11: // Self insured
            case 15: // Cosmetic
            default:
                break;
        }
    };

    const _loadInsuranceFund = async () => {
        if ($insuranceFund.length)
            insuranceObj.insuranceFund = $insuranceFund.find(
                (fund) => fund.id == modalBillingData.patientInsuranceId,
            );
        else if (!isInsuranceFundFetched) {
            isInsuranceFundFetched = true;
            isLoadingInsuranceFund = true;

            await cacheInsuranceFunds();

            _loadInsuranceFund();

            isLoadingInsuranceFund = false;
        }
    };

    const _checkInputFieldsValid = () => {
        if (!isValidMedicareNo) return false;

        if (insuranceObj.insuranceType == unknownPatientInsuranceType)
            return true;

        if (insuranceObj.insuranceType == 0)
            return insuranceObj.insuranceFund?.id && insuranceObj.membershipNo;
        else if (
            insuranceObj.insuranceType == 10 ||
            insuranceObj.insuranceType == 11 ||
            insuranceObj.insuranceType == 15
        )
            return true;

        return false;
    };

    const _updateInvalidFieldsStyle = () => {
        if (!isValidMedicareNo) _setErrorStyle("medicareNoField");

        if (insuranceObj.insuranceType == 0) {
            if (!insuranceObj.insuranceFund?.id)
                _setErrorStyle("insuranceFundContainer");
            if (!insuranceObj.membershipNo) _setErrorStyle("membershipNoInput");
        }
    };

    const _clearInsuranceFund = () => {
        insuranceObj.insuranceFund = null;

        _resetErrorStyle("insuranceFundContainer");
    };

    const _handleSelectInsuranceFund = () => {
        insuranceObj.privateCoverType = isAmaInsurance
            ? "Normal Cover"
            : isAuhInsurance
              ? "Basic Plus"
              : "";
    };

    const _setErrorStyle = (fieldId = "") => {
        const field = document.getElementById(`${fieldId}`);

        if (field) field.style.outline = "red 2px solid";
    };

    const _resetErrorStyle = (fieldId = "") => {
        const field = document.getElementById(`${fieldId}`);

        field.style.outline = "none";
    };

    const _checkInsuranceTypeEnableSwitchItemType = (
        insuranceType,
        modalData,
    ) => {
        const isSelectASACode = modalData.useAsaCodes;

        const notAllowAsaArr = [...$mbsToAsaDisallowAsaByInsuranceType] ?? [
            0, 11,
        ]; // Private, Self-insured

        if (
            insuranceType === undefined ||
            !notAllowAsaArr.includes(Number(insuranceType))
        )
            return true;
        else if (notAllowAsaArr.includes(Number(insuranceType))) {
            if (isSelectASACode) openInsuranceItemConflictModal();
            else return false;
        }
    };

    const _getItemTypeTooltipText = () => {
        return "Private and Self-insured insurances are not support ASA items";
    };

    const _getModalBillingDataTotalFee = (
        addGST,
        actualGap,
        rebate,
        totalFee,
    ) => {
        if (!actualGap) actualGap = Number(0);
        if (!rebate) rebate = Number(0);

        if (totalFee) {
            const displayTotalFee =
                Number(totalFee) * (addGST ? Number(1.1) : Number(1));

            return getAmountInCurrency(displayTotalFee);
        }

        modalBillingData.totalFee = Number(actualGap + rebate);

        const displayTotalFee = Number(
            (actualGap + rebate) * (addGST ? Number(1.1) : Number(1)),
        );

        return getAmountInCurrency(displayTotalFee);
    };

    const _getDisplayBillingTypes = (
        timeItemExists = false,
        isWcOrTacUsingAmaRates = false,
    ) => {
        if (isWcOrTacUsingAmaRates) {
            const amaRatesBillingRule = {
                id: 2,
                type: "AMA Rates",
            };
            modalBillingData.billingType = amaRatesBillingRule.id;
            modalBillingData.calculationAmount =
                modalBillingData.calculationAmount
                    ? modalBillingData.calculationAmount
                    : $amaUnitValue;
            return [{ ...amaRatesBillingRule }];
        } else {
            const temp = BillingType.filter(
                (type) => type.id != 0 && type.id != 1 && type.id != 6,
            );

            return timeItemExists
                ? [...temp]
                : temp.filter((type) => type.id != 4);
        }
    };

    const isPastEncounter = (scheduledDate) => {
        if (!scheduledDate) return true;

        const scheduledTime = new Date(scheduledDate).getTime();
        const today = new Date();
        // today.setHours(0, 0, 0);
        return scheduledTime < today.getTime();
    };
</script>

<NotificationModal
    {isModalOpen}
    title={`${modalBillingData.id ? "Edit" : "Create"} Quote Billing Data`}
    cancelText="Cancel"
    modalMaxWidth="max-w-6xl"
    showConfirmBtn={false}
    closeModalFunction={closeModal}
    {closeModal}
    isConfirming={isUpdatingBillingDetails}
    noContentPadding
    btnOptions={[
        {
            text: `${
                modalBillingData.id
                    ? isUpdatingBillingDetails
                        ? "Updating"
                        : "Update"
                    : isUpdatingBillingDetails
                      ? "Creating"
                      : "Create"
            }`,
            loading: isUpdatingBillingDetails || invalidDateRange,
            function: () => updateProcess(),
        },
    ]}
>
    <div class="flex flex-col flex-1 overflow-y-auto p-6">
        <div class="flex flex-1 gap-x-4">
            <div class="flex flex-1 justify-start flex-col gap-y-4">
                <div class="flex gap-x-4">
                    <p class="w-48">OOP Type:</p>
                    <select
                        class="input input-bordered input-sm w-60"
                        disabled={isUpdatingBillingDetails ||
                            isChangingBillingTypeDisabled}
                        bind:value={modalBillingData.oopType}
                    >
                        {#each OOPType as oop}
                            <option value={oop.id}>
                                {oop.type}
                            </option>
                        {/each}
                    </select>
                </div>

                <div class="flex gap-x-4">
                    <p class="w-48">Patient Pays Total Fee:</p>
                    <select
                        class="input input-bordered input-sm w-60"
                        disabled={isUpdatingBillingDetails}
                        bind:value={modalBillingData.patientPaysTotalFee}
                    >
                        <option value={Boolean(true)}> YES </option>
                        <option value={Boolean(false)}>NO</option>
                    </select>
                </div>

                <div class="flex gap-x-4">
                    <div class="flex w-48 gap-x-1 h-8 items-center">
                        <p>Items type:</p>
                        {#if !isInsuranceTypeEnableSwitchItemType}
                            <button
                                class="btn btn-ghost btn-circle tooltip tooltip-secondary tooltip-right"
                                data-tip={_getItemTypeTooltipText()}
                            >
                                <i class="fa-solid fa-circle-question" />
                            </button>
                        {/if}
                    </div>
                    <div class="flex flex-1 items-center gap-x-4">
                        <div class="flex items-center gap-x-2">
                            <input
                                type="radio"
                                name="radio-10"
                                class="radio checked:bg-secondary"
                                checked={!modalBillingData.useAsaCodes}
                                on:change={() => {
                                    modalBillingData.useAsaCodes = false;
                                    handleItemsTypeChange();
                                }}
                            />
                            <p>MBS</p>
                        </div>

                        <div class="flex items-center gap-x-2">
                            <input
                                type="radio"
                                name="radio-10"
                                class="radio checked:bg-secondary"
                                checked={modalBillingData.useAsaCodes}
                                on:change={() => {
                                    modalBillingData.useAsaCodes = true;
                                    handleItemsTypeChange();
                                }}
                                disabled={!isInsuranceTypeEnableSwitchItemType}
                            />
                            <p>ASA</p>
                        </div>
                    </div>
                </div>

                <div class="flex gap-x-4">
                    <p class="w-48">Within Fund Rules:</p>
                    <select
                        class="input input-bordered input-sm w-60"
                        bind:value={modalBillingData.withinFundRules}
                    >
                        <option value={true}>True</option>
                        <option value={false}>False</option>
                    </select>
                </div>

                <div class="flex flex-col gap-y-2">
                    <div class="flex gap-x-4 items-center">
                        <p class="w-48">Start time:</p>
                        <DateInput
                            id="startDateInput"
                            showTime
                            wrapperClass="input input-bordered input-sm w-60 px-0"
                            wrapperClassFocus="inputFocus"
                            bind:value={modalBillingData.startDate}
                            max={modalBillingData.endDate
                                ? modalBillingData.endDate
                                : ""}
                        />
                    </div>

                    {#if invalidDateRange}
                        <div class="flex gap-x-4">
                            <p class="text-error">Invalid date range</p>
                        </div>
                    {/if}
                </div>
            </div>

            <div class="divider divider-horizontal" />

            <div class="flex flex-1 justify-start flex-col gap-y-4">
                {#if insuranceAllowManuallyChangeBillingRule}
                    <div class="flex gap-x-4">
                        <p class="w-48">Billing Type:</p>
                        <select
                            class="input input-bordered input-sm w-60"
                            bind:value={modalBillingData.billingType}
                            on:change={handleChangeBillingType}
                        >
                            {#each displayBillingTypes as type}
                                <option value={type.id}>{type.type}</option>
                            {/each}
                        </select>
                    </div>
                {/if}

                <div class="flex gap-x-4">
                    <p class="w-48">Add GST:</p>
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={modalBillingData.addGST}
                        disabled
                    />
                </div>

                {#if modalBillingData.billingType == 0}
                    <!-- None -->
                {:else if insuranceAllowManuallyChangeBillingRule}
                    {#if modalBillingData.billingType && modalBillingData.billingType != 6}
                        <div class="flex gap-x-4">
                            <p class="w-48">
                                {modalBillingData.billingType == 1 ||
                                modalBillingData.billingType == 3
                                    ? "Actual Gap:"
                                    : modalBillingData.billingType == 2
                                      ? isWcOrTacUsingAmaRates
                                          ? "AMA Rates"
                                          : "Unit Value:"
                                      : modalBillingData.billingType == 4
                                        ? "Hourly Rate:"
                                        : modalBillingData.billingType == 5
                                          ? "Total Fee:"
                                          : ""}
                            </p>
                            <DecimalInput
                                inputClass="w-60"
                                bind:value={modalBillingData.calculationAmount}
                                isDisabled={modalBillingData.billingType == 1}
                            />
                        </div>
                    {:else}
                        <!-- Set MBS Fee Percentage -->
                    {/if}
                {:else}
                    <!-- Hide manually input fields -->
                {/if}

                <div class="flex gap-x-4">
                    <p class="w-48">Rebate:</p>
                    <p class="w-60 pl-4">
                        {getAmountInCurrency(modalBillingData.rebate ?? 0)}
                    </p>
                </div>

                <div class="flex gap-x-4">
                    <p class="w-48">Total Fee:</p>

                    <p class="w-60 pl-4">{displayTotalFee}</p>
                </div>

                <div class="flex gap-x-4 items-center">
                    <p class="w-48">End time:</p>
                    <DateInput
                        id="endDateInput"
                        showTime
                        wrapperClass="input input-bordered input-sm w-60 px-0"
                        wrapperClassFocus="inputFocus"
                        bind:value={modalBillingData.endDate}
                        min={modalBillingData.startDate
                            ? modalBillingData.startDate
                            : ""}
                    />
                </div>
            </div>
        </div>

        <div class="divider" />

        <div class="flex flex-1 flex-col gap-y-4">
            <div class="flex flex-1 gap-x-4">
                <div class="flex flex-1 flex-col gap-y-4">
                    <div class="flex gap-x-4">
                        <p class="w-48">Insurance type:</p>

                        <select
                            class="select select-bordered select-sm w-60"
                            bind:value={insuranceObj.insuranceType}
                            on:change={handleChangeBillingType}
                        >
                            {#each restrictedInsuranceTypes as insurance}
                                <option
                                    value={insurance.id}
                                    selected={insurance.id ==
                                        insuranceObj.insuranceType}
                                >
                                    {insurance.type}
                                </option>
                            {/each}
                            {#if insuranceObj.insuranceType == unknownPatientInsuranceType}
                                <option
                                    value={unknownPatientInsuranceType}
                                    selected
                                    disabled>Unknown</option
                                >
                            {/if}
                        </select>
                    </div>

                    {#if insuranceObj.insuranceType != 10 && insuranceObj.insuranceType != unknownPatientInsuranceType}
                        <div class="flex gap-x-4">
                            <p class="w-48">Medicare No.:</p>

                            <input
                                type="text"
                                id="medicareNoField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.medicareNo}
                                on:focus={_resetErrorStyle("medicareNoField")}
                                on:input={({ target }) => {
                                    insuranceObj.medicareNo = target.value
                                        .replaceAll(" ", "")
                                        .replaceAll("-", "");
                                }}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">Medicare Ref No.:</p>

                            <input
                                type="text"
                                id="medicareRefNoField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.medicareRefNo}
                                on:focus={_resetErrorStyle(
                                    "medicareRefNoField",
                                )}
                            />
                        </div>
                    {/if}
                </div>

                <div class="divider divider-horizontal" />

                <div class="flex flex-1 flex-col gap-y-4">
                    {#if insuranceObj.insuranceType == 0}
                        <div class="flex gap-x-4">
                            <p class="w-48">Insurance Fund:</p>

                            {#if isLoadingInsuranceFund}
                                <div class="flex gap-x-2">
                                    <Loader />
                                    <p>Getting options</p>
                                </div>
                            {:else if !$insuranceFund.length}
                                <p class="w-60">No Insurance Fund found</p>
                            {:else}
                                <div class="w-60" id="insuranceFundContainer">
                                    <Select
                                        bind:value={insuranceObj.insuranceFund}
                                        class="flex flex-1 max-h-[35px]"
                                        items={$insuranceFund}
                                        itemId="id"
                                        label="name"
                                        on:clear={_clearInsuranceFund}
                                        on:select={_handleSelectInsuranceFund}
                                        noOptionsMessage="No Insurance found"
                                        placeholder="Insurance fund"
                                    />
                                </div>
                            {/if}
                        </div>

                        {#if showCoverTypeForPrivateInsurance}
                            <div class="flex gap-x-4">
                                <p class="w-48">Cover Type:</p>

                                {#if isAmaInsurance}
                                    <select
                                        class="select select-bordered select-sm w-60"
                                        bind:value={insuranceObj.privateCoverType}
                                    >
                                        <option
                                            value="Normal Cover"
                                            selected={insuranceObj.privateCoverType ==
                                                "Normal Cover" ||
                                                !insuranceObj.privateCoverType}
                                        >
                                            Prime Choice Gold (AHSA RVGs)
                                        </option>
                                        <option
                                            value="Top Cover"
                                            selected={insuranceObj.privateCoverType ==
                                                "Top Cover"}
                                        >
                                            Top Cover Gold (AMA RVGs)
                                        </option>
                                    </select>
                                {:else if isAuhInsurance}
                                    <select
                                        class="select select-bordered select-sm flex flex-1"
                                        bind:value={insuranceObj.privateCoverType}
                                    >
                                        <option
                                            value="Basic Plus"
                                            selected={insuranceObj.privateCoverType ==
                                                "Basic Plus" ||
                                                !insuranceObj.privateCoverType}
                                        >
                                            Basic Plus
                                        </option>
                                        <option
                                            value="Core/Standard"
                                            selected={insuranceObj.privateCoverType ==
                                                "Core/Standard"}
                                        >
                                            Core/Standard
                                        </option>
                                    </select>
                                {/if}
                            </div>
                        {/if}

                        <div class="flex gap-x-4">
                            <p class="w-48">Membership No.:</p>

                            <input
                                type="text"
                                id="membershipNoInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.membershipNo}
                                on:focus={() =>
                                    _resetErrorStyle("membershipNoInput")}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">Membership Ref No.:</p>

                            <input
                                type="text"
                                id="membershipRefNoInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.membershipRefNo}
                                on:focus={() =>
                                    _resetErrorStyle("membershipRefNoInput")}
                            />
                        </div>
                    {/if}
                </div>
            </div>

            {#if insuranceObj.insuranceType == 10}
                <div class="flex flex-1 gap-x-4">
                    <p class="w-48">Overseas Cover:</p>

                    <div class="flex flex-1">
                        <input
                            type="text"
                            id="overseasCoverField"
                            class="input input-bordered input-sm w-full"
                            autocomplete="off"
                            bind:value={insuranceObj.overseasCover}
                            on:focus={() =>
                                _resetErrorStyle("overseasCoverField")}
                        />
                    </div>
                </div>
            {/if}
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="You are about to change Item type"
    cancelText="Cancel"
    submitText={isUpdatingBillingDetails ? "Updating" : "Confirm"}
    isModalOpen={changeItemTypeModalOpen}
    closeModal={() => closeChangeItemTypeModal()}
    confirmAction={changeItemType}
    isConfirming={isUpdatingBillingDetails}
    closeModalFunction={closeChangeItemTypeModal}
>
    <p class="leading-6 whitespace-pre-line">
        Change items type to {modalBillingData.useAsaCodes
            ? "ASA"
            : "MBS"}?
    </p>
    <p class="mt-4 leading-6 whitespace-pre-line">
        Confirming will change the items type and remove all the
        existing billing data items.
    </p>
</NotificationModal>

<NotificationModal
    title="AMA codes are not allowed for this insurance type."
    cancelText="Close"
    submitText="Confirm"
    isModalOpen={isShowInsuranceItemConflictModal}
    closeModal={() => closeInsuranceItemConflictModal()}
    closeModalFunction={closeInsuranceItemConflictModal}
    confirmAction={() => closeInsuranceItemConflictModal(true)}
>
    <div class="flex flex-col gap-y-4">
        <p class="leading-6 whitespace-pre-line">
            Change items type to MBS?
        </p>
        <p class="leading-6">
            Confirming will change the items type to MBS and remove all the
            existing billing data items.
        </p>
    </div>
</NotificationModal>
